import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../../styles/theme';
import { Container } from '../common/Container';
import { Button } from '../common/Button';

const SectionWrapper = styled.section`
  padding: ${theme.spacing['4xl']} 0;
  background-color: ${theme.colors.backgroundSecondary};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['3xl']};
`;

const Title = styled.h2`
  font-size: ${theme.fontSizes['3xl']};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto;
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing['3xl']};

  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StepCard = styled(motion.div)`
  background-color: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing.xl};
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const StepNumber = styled.div`
  width: 48px;
  height: 48px;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.text.inverse};
  border-radius: ${theme.radii.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 auto ${theme.spacing.lg};
`;

const StepIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${theme.spacing.md};
`;

const StepTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
`;

const StepDescription = styled.p`
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
`;

const DemoSection = styled.div`
  background-color: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing['2xl']};
  margin-top: ${theme.spacing['3xl']};
`;

const DemoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing.xl};

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
    align-items: center;
  }
`;

const BeforeAfterCard = styled.div`
  background-color: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.lg};
`;

const Label = styled.span`
  display: inline-block;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background-color: ${props => props.color || theme.colors.surface};
  color: ${theme.colors.text.primary};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  margin-bottom: ${theme.spacing.md};
`;

const PromptText = styled.p`
  font-family: ${theme.fonts.code};
  line-height: 1.6;
  color: ${theme.colors.text.primary};
`;

const ArrowIcon = styled.div`
  font-size: 2rem;
  color: ${theme.colors.primary};
  text-align: center;
  
  @media (min-width: ${theme.breakpoints.lg}) {
    transform: rotate(-90deg);
  }
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: ${theme.spacing['3xl']};
`;

export const HowItWorks: React.FC = () => {
  const steps = [
    {
      number: '1',
      icon: '⬇️',
      title: 'Install Extension',
      description: 'Add Promptasaurus to Chrome with one click. Works seamlessly with ChatGPT and Claude.',
    },
    {
      number: '2',
      icon: '✍️',
      title: 'Write Your Prompt',
      description: 'Type your prompt as usual. Our extension detects when you\'re ready to send.',
    },
    {
      number: '3',
      icon: '✨',
      title: 'Click Improve',
      description: 'Hit the "Improve" button to transform your prompt into a comprehensive, context-rich instruction.',
    },
  ];

  return (
    <SectionWrapper>
      <Container>
        <SectionHeader>
          <Title>How It Works</Title>
          <Subtitle>
            Transform your prompts in three simple steps. No API keys, no complex setup.
          </Subtitle>
        </SectionHeader>

        <StepsGrid>
          {steps.map((step, index) => (
            <StepCard
              key={step.number}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <StepNumber>{step.number}</StepNumber>
              <StepIcon>{step.icon}</StepIcon>
              <StepTitle>{step.title}</StepTitle>
              <StepDescription>{step.description}</StepDescription>
            </StepCard>
          ))}
        </StepsGrid>

        <DemoSection>
          <SectionHeader>
            <h3>See the Difference</h3>
          </SectionHeader>
          <DemoGrid>
            <BeforeAfterCard>
              <Label color="#FEE2E2">Before</Label>
              <PromptText>
                "Write a blog post about climate change"
              </PromptText>
            </BeforeAfterCard>
            
            <ArrowIcon>→</ArrowIcon>
            
            <BeforeAfterCard>
              <Label color="#D1FAE5">After</Label>
              <PromptText>
                "You are an environmental science writer with expertise in climate research. 
                Write a comprehensive 800-word blog post about climate change that:
                • Explains the current state of global warming
                • Discusses recent scientific findings
                • Provides actionable steps readers can take
                • Uses accessible language for a general audience
                • Includes relevant statistics from reputable sources"
              </PromptText>
            </BeforeAfterCard>
          </DemoGrid>
        </DemoSection>

        <CTASection>
          <h3 style={{ marginBottom: theme.spacing.lg }}>Ready to enhance your AI conversations?</h3>
          <Button size="large">
            Get the Extension - It's Free!
          </Button>
        </CTASection>
      </Container>
    </SectionWrapper>
  );
};